import "../styles/BrandStorySections.css";

const steps = [
  {
    number: "01",
    icon: "💬",
    title: "Chat your vision",
    description: "Tell our AI about your business, your audience, and the feeling you want your brand to have.",
  },
  {
    number: "02",
    icon: "🎨",
    title: "Get your identity",
    description: "AI generates logos, colors, typography, and a brand personality tailored just for you.",
  },
  {
    number: "03",
    icon: "🌐",
    title: "Claim your domain",
    description: "Find the perfect domain that fits your brand. We'll suggest names and check availability instantly.",
  },
  {
    number: "04",
    icon: "🚀",
    title: "Launch your site",
    description: "Your AI-designed website is ready to publish. One click and you're live with hosting.",
  },
];

const pillars = [
  {
    key: "define",
    title: "Define",
    subtitle: "Claim your identity",
    description: "Your domain is your address on the internet and your first impression. We make finding and claiming the right one fast, smart, and permanent.",
    tags: ["Domain Search", "AI Generator", "Bulk Search", "WHOIS", "Aftermarket", "Link in Bio"],
  },
  {
    key: "protect",
    title: "Protect",
    subtitle: "Guard what's yours",
    description: "Your domain is your most valuable digital asset. We keep it locked, monitored, renewed, and shielded so it's never at risk.",
    tags: ["SSL Certificates", "SiteLock", "Domain Privacy", "Expiration Guard", "Trademark"],
  },
  {
    key: "amplify",
    title: "Amplify",
    subtitle: "Grow your presence",
    description: "Getting online is the beginning. We give you tools, expertise, and support to build a presence people find, trust, and remember.",
    tags: ["AI Website Builder", "eCommerce", "SEO Tools", "PPC Ads", "Directories", "Google Workspace"],
  },
];

export default function BrandStorySections() {
  return (
    <div className="wc">
      <h2 className="wc-sr-only">Web.com full page sections</h2>

      <section className="wc-process">
        <div className="wc-process-header">
          <div>
            <span className="wc-process-eyebrow">Process</span>
            <h2>From idea to live brand<br />in four steps.</h2>
          </div>
          <p>No design degree needed. Just describe what you're building and let AI do the heavy lifting.</p>
        </div>

        <div className="wc-steps">
          {steps.map((step) => (
            <div className="wc-step" key={step.number}>
              <div className="wc-step-num">{step.number}</div>
              <span className="wc-step-icon" aria-hidden="true">{step.icon}</span>
              <p className="wc-step-title">{step.title}</p>
              <p className="wc-step-desc">{step.description}</p>
            </div>
          ))}
        </div>

        <div className="wc-features">
          <div className="wc-feat-card">
            <div className="wc-feat-card-head">
              <div className="wc-feat-label">Domain Search</div>
              <p className="wc-feat-title">Your perfect name, found instantly.</p>
              <p className="wc-feat-desc">AI suggests domain names that fit your brand personality and checks real-time availability across all extensions.</p>
            </div>
            <div className="wc-feat-preview">
              <div className="wc-domain-row"><span className="wc-domain-name">bloomroast.com</span><span className="wc-domain-badge badge-avail">Available - $12/yr</span></div>
              <div className="wc-domain-row"><span className="wc-domain-name">bloomroast.co</span><span className="wc-domain-badge badge-avail">Available - $18/yr</span></div>
              <div className="wc-domain-row"><span className="wc-domain-name wc-domain-taken">bloomroasters.com</span><span className="wc-domain-badge badge-taken">Taken</span></div>
              <div className="wc-domain-row"><span className="wc-domain-name">getbloomroast.com</span><span className="wc-domain-badge badge-avail">Available - $12/yr</span></div>
            </div>
          </div>

          <div className="wc-feat-card">
            <div className="wc-feat-card-head">
              <div className="wc-feat-label">Logo Generator</div>
              <p className="wc-feat-title">A logo worth loving, in seconds.</p>
              <p className="wc-feat-desc">Choose from dozens of AI-crafted logo concepts. Refine with a single message until it's exactly right.</p>
            </div>
            <div className="wc-feat-preview">
              <div className="wc-logo-grid">
                <div className="wc-logo-tile lt1">☕</div>
                <div className="wc-logo-tile lt2">🌿</div>
                <div className="wc-logo-tile lt3">🌸</div>
                <div className="wc-logo-tile lt4">✦</div>
                <div className="wc-logo-tile lt5">⬡</div>
                <div className="wc-logo-tile lt6">◈</div>
              </div>
              <p className="wc-logo-caption">6 of 24 concepts generated for "Bloom Roast"</p>
            </div>
          </div>

          <div className="wc-feat-card">
            <div className="wc-feat-card-head">
              <div className="wc-feat-label">Website Builder</div>
              <p className="wc-feat-title">A website that matches your brand, automatically.</p>
              <p className="wc-feat-desc">AI builds your website using your brand colors, fonts, and voice. Edit in real time through chat, or click to customize any element.</p>
              <div className="wc-feat-tags">
                <span className="wc-feat-tag">Mobile Optimized</span>
                <span className="wc-feat-tag">SEO Ready</span>
                <span className="wc-feat-tag">Hosting Included</span>
              </div>
            </div>
            <div className="wc-feat-preview">
              <div className="wc-site-bar"><span className="dot-red"></span><span className="dot-yellow"></span><span className="dot-green"></span></div>
              <div className="wc-site-url">bloomroast.com</div>
              <div className="wc-site-hero-text">Roasted with care. Shipped to your door.</div>
              <div className="wc-site-hero-sub">Single origin coffee, freshly roasted weekly.</div>
              <span className="wc-site-btn">Shop Blends</span>
            </div>
          </div>

          <div className="wc-feat-card">
            <div className="wc-feat-card-head">
              <div className="wc-feat-label">Brand Colors & Fonts</div>
              <p className="wc-feat-title">A palette that tells your story.</p>
              <p className="wc-feat-desc">AI selects a cohesive color system and font pairing that reflects your brand's personality and stands out in your market.</p>
            </div>
            <div className="wc-feat-preview">
              <div className="wc-colors-row">
                <div className="wc-swatch swatch-forest"></div>
                <div className="wc-swatch swatch-leaf"></div>
                <div className="wc-swatch swatch-gold"></div>
                <div className="wc-swatch swatch-cream"></div>
                <div className="wc-swatch swatch-espresso"></div>
              </div>
              <div>
                <div className="wc-font-display">Bloom Roast</div>
                <div className="wc-font-sub">Display - Instrument Serif · Single origin. Always fresh. - DM Sans Light</div>
              </div>
            </div>
          </div>
        </div>

        <div className="wc-stats">
          <div className="wc-stat"><div className="wc-stat-num">2M<span>+</span></div><div className="wc-stat-label">Brands launched on Web.com</div></div>
          <div className="wc-stat"><div className="wc-stat-num">8<span> min</span></div><div className="wc-stat-label">Average time from idea to live brand</div></div>
          <div className="wc-stat"><div className="wc-stat-num">98<span>%</span></div><div className="wc-stat-label">Of customers would recommend us</div></div>
        </div>
      </section>

      <section className="wc-philosophy">
        <div className="wc-phil-header">
          <div>
            <span className="wc-phil-eyebrow">Our Philosophy</span>
            <h2>Three words that guide<br />everything <em>we do</em></h2>
          </div>
          <p className="wc-phil-desc">Every product, every feature, every support call maps back to this framework.</p>
        </div>
        <div className="wc-pillars">
          {pillars.map((pillar) => (
            <div className="wc-pillar" key={pillar.key}>
              <div className={`wc-pillar-blob blob-${pillar.key}`}></div>
              <p className={`wc-pillar-title title-${pillar.key}`}>{pillar.title}</p>
              <p className="wc-pillar-sub">{pillar.subtitle}</p>
              <p className="wc-pillar-desc">{pillar.description}</p>
              <div className="wc-pillar-tags">
                {pillar.tags.map((tag) => (
                  <button className="wc-ptag" type="button" key={tag}>{tag}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="wc-history">
        <div className="wc-history-inner">
          <div className="wc-hist-body">
            <span className="wc-hist-eyebrow">Our History</span>
            <h2>40 years on the front line of the <em>internet</em></h2>
            <p>We registered the first commercial domain names. We've been here since before Google - and we'll be here long after.</p>
            <p>That history means deep institutional knowledge, long-standing partnerships, and infrastructure tested by time - not just SLA documents.</p>
          </div>
          <div className="wc-hist-stats">
            <div className="wc-hist-stat"><div className="wc-hist-stat-num">1979</div><div className="wc-hist-stat-label">Founded before the web</div></div>
            <div className="wc-hist-stat"><div className="wc-hist-stat-num">16M+</div><div className="wc-hist-stat-label">Domains managed globally</div></div>
            <div className="wc-hist-stat"><div className="wc-hist-stat-num">180+</div><div className="wc-hist-stat-label">Countries reached</div></div>
            <div className="wc-hist-stat"><div className="wc-hist-stat-num">99.9%</div><div className="wc-hist-stat-label">Uptime SLA</div></div>
          </div>
        </div>
      </section>
    </div>
  );
}
